
import { defineComponent, ref, nextTick } from 'vue';
import MiniLesson, { MiniLessonActivity } from '@/model/MiniLesson';
import ActivityVideoPlayer from '@/components/elements/ActivityVideoPlayer.vue';
import SegmentedProgressBar, { calculateProgressSegments, ProgressSegmentData } from '@/components/elements/SegmentedProgressBar.vue';
import webcamProvider from '@/services/WebcamProvider';
import Utils from '@/services/Utils';

export default defineComponent({
  name: 'MiniLessonPlayer',
  components: {
    SegmentedProgressBar,
    ActivityVideoPlayer,
  },
  props: {
    videoEntry: { type: Object },
    miniLesson: { type: Object },
    // maxVideoHeight: { type: String, default: 'none' },
    enableCompleteLesson: { type: Boolean, default: false },
  },
  setup() {
    const activityVideoPlayer = ref(null as null | typeof ActivityVideoPlayer);
    return {
      activityVideoPlayer,
    };
  },
  emits: ['lesson-completed', 'activity-changed'],
  computed: {
    nearbyActivityIndices(): number[] {
      const margin = 1;
      const minIndex = Math.max(0, this.activeActivityIndex - margin);
      const maxActivityIndex = (this.lesson?.activities.length ?? 0) - 1;
      const maxIndex = Math.min(maxActivityIndex, this.activeActivityIndex + margin);
      const count = (maxIndex + 1) - minIndex;
      let indices: number[] = [];
      if (minIndex > 0) indices = indices.concat([0, -1]);
      indices = indices.concat(Utils.range(count, minIndex));
      if (maxIndex < maxActivityIndex) indices = indices.concat([-1, maxActivityIndex]);
      return indices;
    },
    lesson() {
      const lesson = (this.$props.miniLesson ?? null) as null | MiniLesson;
      return lesson;
    },
    activities(): MiniLessonActivity[] {
      return (this.lesson as any)?.activities ?? [];
    },
    activeActivity() {
      const lesson = (this.$props.miniLesson ?? null) as null | MiniLesson;
      const activityIndex = this.activeActivityIndex as number;
      const activity = lesson?.activities[activityIndex] ?? null as null | MiniLessonActivity;
      return activity;
    },
    progressSegments(): ProgressSegmentData[] {
      if (this.activeActivity && this.lesson) return calculateProgressSegments(this.lesson, this.activeActivity);
      return [];
    },
    hasNextActivity(): boolean {
      return this.activeActivityIndex + 1 < (this.lesson?.activities.length ?? 0);
    },
    hasPreviousActivity(): boolean {
      return this.activeActivityIndex > 0;
    },
    needsStartWebcam(): boolean {
      return this.activeActivity !== null && this.activeActivity?.userVisual !== 'none' && webcamProvider.webcamStatus.value !== 'running';
    },
  },
  data() {
    return {
      activeActivityIndex: 0,
      activityProgress: 0,
      webcamStatus: webcamProvider.webcamStatus,
    };
  },
  methods: {
    onProgress(val: number) { this.activityProgress = val; },
    autoPlay() {
      nextTick(() => {
        if (!this.needsStartWebcam) {
          this.playActivity();
        }
      });
    },
    playActivity() { (this.$refs.activityVideoPlayer as any).play(); },
    nextActivity() {
      if (this.hasNextActivity) this.activeActivityIndex += 1;
      nextTick(() => {
        if (!this.needsStartWebcam) this.playActivity();
      });
    },
    completeLesson() {
      this.$emit('lesson-completed');
    },
    gotoActivity(i: number) {
      this.activeActivityIndex = i;
      this.autoPlay();
    },
    async startWebcam() {
      return (this.$refs.activityVideoPlayer as any)?.startWebcam();
    },
    repeat() {
      const vidPlayer = (this.$refs.activityVideoPlayer as any);
      vidPlayer.reset();
      vidPlayer.play();
    },
  },
  watch: {
    activeActivityIndex(newVal: number) {
      this.$emit('activity-changed', newVal);
    },
  },
});
